import type { SettingsEvent } from "@/features/settings/mediators/settingsEvents";
import {
  DEFAULT_SPEECH_SETTINGS,
  type SpeechSettings,
} from "@/features/thread/utils/speech";
import { createMediator, type Mediator } from "@/shared/lib/componentHierarchy";

export type SpeechSettingsEvent = Extract<
  SettingsEvent,
  {
    type:
      | "settings/speech-boolean-changed"
      | "settings/speech-number-changed"
      | "settings/speech-reset";
  }
>;

function clampNumber(field: "port" | "rate" | "volume", value: string): number {
  const parsed = Number(value);
  if (field === "port") return Math.max(1, Math.min(65535, parsed || 50080));
  if (field === "rate") return Math.max(0.5, Math.min(2, parsed || 1));
  return Math.max(0, Math.min(1, parsed));
}

export function createSpeechSettingsMediator(
  initialState: SpeechSettings,
  persist: (settings: SpeechSettings) => void,
): Mediator<SpeechSettings, SpeechSettingsEvent, SpeechSettings> {
  return createMediator<SpeechSettings, SpeechSettingsEvent, SpeechSettings>(
    initialState,
    (state, event) => {
      const nextState =
        event.type === "settings/speech-reset"
          ? DEFAULT_SPEECH_SETTINGS
          : event.type === "settings/speech-boolean-changed"
            ? { ...state, [event.field]: event.value }
            : {
                ...state,
                [event.field]: clampNumber(event.field, event.value),
              };
      return {
        status: "handled",
        state: nextState,
        effects: [nextState],
      };
    },
    { runEffect: persist },
  );
}
